import {
  ClubRef,
  DashboardClubCoach,
  DashboardClubDetail,
  DashboardClubListItem,
  DashboardClubMemberListItem,
  DashboardClubMemberStats,
  DashboardClubMemberStatus,
  DashboardClubRef,
  DashboardClubType,
} from "@/api/__generated__/schemas";
import {
  BaseClubFragment,
  ClubMemberFragment,
  ClubMemberStatsFragment,
  ClubServiceFragment,
  ClubServiceResourceFragment,
  ClubWithCoachesFragment,
  ClubWithFederationFragment,
  ClubWithMembersCountFragment,
  ClubWithPlatformsFragment,
  ClubWithTournamentsCountFragment,
  fromApiDate,
  Monolith_ClubType,
} from "@/graphql";
import { ApiClub, ApiClubStatus } from "@/graphql/types";
import { mapToPlatformCoach, mapToPlatformCoachWithStudents } from "@/mappers/coach.mappers";
import { mapAvatarFields, mapToCountryCodeNullable } from "@/mappers/common.mappers";
import { mapToOrganization } from "@/mappers/organization.mappers";
import { mapToBasePlatform } from "@/mappers/platform.mappers";
import { mapToScopePair } from "@/mappers/scope.mappers";
import { mapToSport, mapToSportRef } from "@/mappers/sport.mappers";
import { mapApiUserToBaseUser, mapToUser } from "@/mappers/user.mappers";
import {
  ClubCoach,
  ClubDetail,
  ClubListItem,
  ClubMemberListItem,
  ClubReference,
  BaseClub,
  ClubMember,
  ClubMemberRole,
  ClubMemberStats,
  ClubMemberStatus,
  ClubService,
  ClubServiceResource,
  ClubStatus,
  ClubType,
  ClubWithCoaches,
  ClubWithMembersCount,
  ClubWithOrganization,
  ClubWithPlatforms,
  ClubWithTournamentsCount,
} from "@/types/club.types";

export const mapToBaseClub = (fragment: BaseClubFragment): BaseClub => ({
  id: String(fragment.id),
  name: fragment.clubName!,
  clubName: fragment.clubName!,
  contactEmail: fragment.contactEmail!,
  logoFile: null,
  legacyLogoUrl: fragment.logo ?? null,
  city: fragment.city!,
  country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
  status: mapToClubStatus(fragment.status!),
  state: fragment.state ?? null,
  address: fragment.address!,
  addressCoordinates: fragment.addressCoordinates,
  howToFindUs: fragment.howToFindUs ?? null,
  contactPhone: fragment.contactPhone ?? null,
  instagramLink: fragment.instagramLink ?? null,
  facebookLink: fragment.facebookLink ?? null,
  twitterLink: fragment.twitterLink ?? null,
  tikTok: fragment.tikTok ?? null,
  linkedin: fragment.linkedin ?? null,
  website: fragment.website ?? null,
  isAthleteClub: fragment.isAthleteClub ?? false,
  description: fragment.description ?? null,
  type: fragment.type ? mapToClubType(fragment.type) : null,
  isEnableEmail: fragment.isEnableEmail ?? false,
  isAdminVerificationRequired: fragment.isAdminVerificationRequired ?? false,
  mainAdministrator: fragment.clubMainAdministrator
    ? mapToUser(fragment.clubMainAdministrator)
    : null,
  sports: fragment.sports?.map(mapToSport) ?? [],
});

export const mapToClubType = (apiType: Monolith_ClubType): ClubType => {
  switch (apiType) {
    case Monolith_ClubType.Municipality:
      return "municipality";
    case Monolith_ClubType.Community:
      return "community";
    case Monolith_ClubType.EducationalInstitution:
      return "educationalInstitution";
    // The GraphQL Monolith enum is not regenerated for the new baseline `CLUB`
    // yet, so a `CLUB` value (or any future/unknown type) arrives here at
    // runtime and falls through to the baseline "club" — never mislabelled.
    default:
      return "club";
  }
};

export const mapToApiClubType = (type: ClubType): Monolith_ClubType => {
  switch (type) {
    // The GraphQL Monolith schema has no CLUB member (only the REST DashboardClubType does),
    // so the baseline "club" type collapses to the historical default when written via GraphQL.
    case "club":
      return Monolith_ClubType.Municipality;
    case "municipality":
      return Monolith_ClubType.Municipality;
    case "community":
      return Monolith_ClubType.Community;
    case "educationalInstitution":
      return Monolith_ClubType.EducationalInstitution;
  }
};

export const mapToClubWithCoaches = (fragment: ClubWithCoachesFragment): ClubWithCoaches => ({
  coaches:
    fragment.coaches?.map((coachFragment) => ({
      ...mapToPlatformCoach(coachFragment),
      ...mapToPlatformCoachWithStudents(coachFragment),
    })) ?? [],
});

export const mapToClubMember = (fragment: ClubMemberFragment): ClubMember => ({
  id: fragment.id,
  user: mapToUser(fragment.user!),
  role: fragment.userRole ? mapToClubMemberRole(fragment.userRole) : null,
  status: fragment.status ? mapToClubMemberStatus(fragment.status) : null,
  joinedAt: fragment.createdAt ? fromApiDate(fragment.createdAt) : null,
});

export const mapToClubWithOrganization = (
  fragment: ClubWithFederationFragment
): ClubWithOrganization => ({
  organization: fragment.federation ? mapToOrganization(fragment.federation) : null,
});

export const mapToClubWithPlatforms = (fragment: ClubWithPlatformsFragment): ClubWithPlatforms => ({
  platforms: fragment.platforms ? fragment.platforms.map(mapToBasePlatform) : [],
});

export const mapToClubWithTournamentsCount = (
  fragment: ClubWithTournamentsCountFragment
): ClubWithTournamentsCount => ({
  tournamentsCount: fragment.tournamentsCount ?? 0,
});

export const mapToClubService = (fragment: ClubServiceFragment): ClubService => ({
  id: fragment.id,
  services: {
    id: fragment.services!.id,
    service: fragment.services!.service!,
    type: fragment.services!.type!,
  },
});

const mapClubMemberStatsFragment = (fragment: ClubMemberStatsFragment): ClubMemberStats => ({
  allMembersCount: fragment.total,
  approvedMembersCount: fragment.approved,
  pendingMembersCount: fragment.pending,
  declinedMembersCount: fragment.declined,
});

const emptyClubMemberStats: ClubMemberStats = {
  allMembersCount: 0,
  approvedMembersCount: 0,
  pendingMembersCount: 0,
  declinedMembersCount: 0,
};

export const mapToClubWithMembersCount = (
  fragment: ClubWithMembersCountFragment
): ClubWithMembersCount =>
  fragment.membersStats ? mapClubMemberStatsFragment(fragment.membersStats) : emptyClubMemberStats;

export const mapToClubServiceResource = (
  fragment: ClubServiceResourceFragment
): ClubServiceResource => ({
  id: fragment.id,
  title: fragment.title!,
  type: fragment.type!,
  clubService: mapToClubService(fragment.clubService!),
});

export const mapToClubStatus = (apiStatus: string): ClubStatus => {
  switch (apiStatus) {
    case "Active":
      return "active";
    default:
      return "inactive";
  }
};

const mapApiClubStatusToClubStatus = (apiStatus: ApiClubStatus): ClubStatus => {
  switch (apiStatus) {
    case "Active":
      return "active";
    default:
      return "inactive";
  }
};

export const mapApiClubToBaseClub = (apiClub: ApiClub): BaseClub => ({
  id: String(apiClub.id),
  name: apiClub.clubName,
  logoFile: null,
  legacyLogoUrl: apiClub.logo ?? null,
  city: apiClub.city!,
  clubName: apiClub.clubName,
  country: apiClub.country ? mapToCountryCodeNullable(apiClub.country) : null,
  status: mapApiClubStatusToClubStatus(apiClub.status),
  contactEmail: apiClub.contactEmail!,
  state: apiClub.state ?? null,
  address: apiClub.address!,
  addressCoordinates: apiClub.addressCoordinates ?? null,
  howToFindUs: apiClub.howToFindUs ?? null,
  contactPhone: apiClub.contactPhone ?? null,
  instagramLink: apiClub.instagramLink ?? null,
  facebookLink: apiClub.facebookLink ?? null,
  twitterLink: apiClub.twitterLink ?? null,
  tikTok: apiClub.tikTok ?? null,
  linkedin: apiClub.linkedin ?? null,
  website: apiClub.website ?? null,
  isAthleteClub: apiClub.isAthleteClub ?? false,
  description: apiClub.description ?? null,
  isEnableEmail: apiClub.isEnableEmail ?? false,
  isAdminVerificationRequired: apiClub.isAdminVerificationRequired ?? false,
  mainAdministrator: apiClub.clubMainAdministrator
    ? mapApiUserToBaseUser(apiClub.clubMainAdministrator)
    : null,
  sports: apiClub.sports?.map(mapToSport) ?? [],
  type: "municipality",
});

export const mapToClubStatusInput = (status: ClubStatus): string => {
  switch (status) {
    case "active":
      return "Active";
    default:
      return "Inactive";
  }
};

export const mapToClubMemberRole = (apiRole: string): ClubMemberRole => {
  switch (apiRole.toLowerCase()) {
    case "member":
      return "member";
    case "coach":
      return "coach";
    case "clubadministrator":
      return "clubAdministrator";
    default:
      console.error("Unexpected club member role: ", apiRole);
      return "member";
  }
};

export const mapToClubMemberStatus = (apiStatus: string): ClubMemberStatus => {
  switch (apiStatus.toLowerCase()) {
    case "confirm":
      return "approved";
    case "pending":
      return "pending";
    case "declined":
      return "declined";
    default:
      return "pending";
  }
};

export const mapToApiClubMemberStatus = (status: ClubMemberStatus): string => {
  switch (status) {
    case "approved":
      return "confirm";
    case "pending":
      return "pending";
    case "declined":
      return "declined";
  }
};

const mapToClubStatusFromRest = (status: string | null | undefined): ClubStatus =>
  status === "ACTIVE" ? "active" : "inactive";

const REST_TO_DOMAIN_TYPE: Record<DashboardClubType, ClubType> = {
  CLUB: "club",
  MUNICIPALITY: "municipality",
  COMMUNITY: "community",
  EDUCATIONAL_INSTITUTION: "educationalInstitution",
};

const mapToClubTypeFromRest = (type: DashboardClubType): ClubType => REST_TO_DOMAIN_TYPE[type];

export const mapToClubReference = (ref: DashboardClubRef): ClubReference => ({
  id: ref.id,
  clubName: ref.clubName ?? "",
  name: ref.clubName ?? "",
  logoFile: null,
  legacyLogoUrl: ref.logo ?? null,
  city: ref.city ?? null,
  type: ref.type ? mapToClubTypeFromRest(ref.type) : null,
});

/**
 * Maps the file-storage `ClubRef` schema (`id`, `title`, `logoFile`,
 * `legacyLogoUrl`) — used by nested relations like the league-athletes list — to
 * the shared `ClubReference` domain type. Carries the raw avatar/logo fields
 * through; resolution happens at the consumption site.
 */
export const mapToClubReferenceFromRef = (ref: ClubRef): ClubReference => ({
  id: ref.id,
  clubName: ref.title ?? "",
  name: ref.title ?? "",
  logoFile: ref.logoFile ? { url: ref.logoFile.url ?? null } : null,
  legacyLogoUrl: ref.legacyLogoUrl ?? null,
  city: null,
  type: null,
});

export const mapToClubMemberStats = (
  stats: DashboardClubMemberStats | null | undefined
): ClubMemberStats => ({
  allMembersCount: stats?.total ?? 0,
  approvedMembersCount: stats?.approved ?? 0,
  pendingMembersCount: stats?.pending ?? 0,
  declinedMembersCount: stats?.declined ?? 0,
});

export const mapToClubListItem = (item: DashboardClubListItem): ClubListItem => ({
  id: item.id,
  internalId: item.internalId,
  clubName: item.clubName ?? "",
  name: item.clubName ?? "",
  logoFile: null,
  legacyLogoUrl: item.logo ?? null,
  address: item.address ?? null,
  city: item.city ?? null,
  country: item.country ? mapToCountryCodeNullable(item.country) : null,
  status: mapToClubStatusFromRest(item.status),
  type: item.type ? mapToClubTypeFromRest(item.type) : null,
  scopes: item.scopes.map(mapToScopePair),
  sports: item.sports.map(mapToSportRef),
  tournamentsCount: item.tournamentsCount ?? 0,
  membersStats: mapToClubMemberStats(item.membersStats),
});

const mapClubCoach = (coach: DashboardClubCoach): ClubCoach => ({
  id: coach.id,
  user: coach.user
    ? {
        id: coach.user.id,
        name: coach.user.name ?? "",
        surname: coach.user.surname ?? "",
        email: coach.user.email ?? "",
        ...mapAvatarFields(coach.user),
      }
    : null,
  userRole: coach.userRole ?? null,
  category: coach.category ?? null,
  dateFrom: coach.dateFrom ?? null,
  dateTo: coach.dateTo ?? null,
  studentsCount: coach.studentsCount ?? 0,
});

export const mapToClubDetail = (detail: DashboardClubDetail): ClubDetail => ({
  id: detail.id,
  internalId: detail.internalId,
  clubName: detail.clubName ?? "",
  name: detail.clubName ?? "",
  logoFile: null,
  legacyLogoUrl: detail.logo ?? null,
  address: detail.address ?? null,
  city: detail.city ?? null,
  country: detail.country ? mapToCountryCodeNullable(detail.country) : null,
  state: detail.state ?? null,
  description: detail.description ?? null,
  contactEmail: detail.contactEmail ?? null,
  contactPhone: detail.contactPhone ?? null,
  website: detail.website ?? null,
  status: mapToClubStatusFromRest(detail.status),
  type: detail.type ? mapToClubTypeFromRest(detail.type) : null,
  twitterLink: detail.twitterLink ?? null,
  facebookLink: detail.facebookLink ?? null,
  instagramLink: detail.instagramLink ?? null,
  tikTok: detail.tikTok ?? null,
  linkedin: detail.linkedin ?? null,
  mainAdministrator: detail.mainAdministrator
    ? {
        id: detail.mainAdministrator.id,
        name: detail.mainAdministrator.name ?? "",
        surname: detail.mainAdministrator.surname ?? "",
        email: detail.mainAdministrator.email ?? "",
        avatar: detail.mainAdministrator.avatar ?? null,
        phone: detail.mainAdministrator.phone ?? null,
      }
    : null,
  scopes: detail.scopes.map(mapToScopePair),
  sports: detail.sports.map(mapToSportRef),
  tournamentsCount: detail.tournamentsCount ?? 0,
  membersStats: mapToClubMemberStats(detail.membersStats),
  coaches: detail.coaches.map(mapClubCoach),
});

const REST_TO_DOMAIN_MEMBER_STATUS: Record<DashboardClubMemberStatus, ClubMemberStatus> = {
  CONFIRM: "approved",
  PENDING: "pending",
  DECLINED: "declined",
};

const DOMAIN_TO_REST_MEMBER_STATUS: Record<ClubMemberStatus, DashboardClubMemberStatus> = {
  approved: "CONFIRM",
  pending: "PENDING",
  declined: "DECLINED",
};

export const mapToClubMemberStatusFromRest = (
  status: DashboardClubMemberStatus
): ClubMemberStatus => REST_TO_DOMAIN_MEMBER_STATUS[status];

export const mapToRestClubMemberStatus = (status: ClubMemberStatus): DashboardClubMemberStatus =>
  DOMAIN_TO_REST_MEMBER_STATUS[status];

export const mapToClubMemberListItem = (item: DashboardClubMemberListItem): ClubMemberListItem => ({
  id: item.id,
  user: {
    id: item.user?.id ?? "",
    name: item.user?.name ?? "",
    surname: item.user?.surname ?? "",
    email: item.user?.email ?? "",
    tournatedTag: item.user?.tournatedTag ?? null,
    ...mapAvatarFields(item.user),
  },
  role: item.userRole ? mapToClubMemberRole(item.userRole) : null,
  status: mapToClubMemberStatusFromRest(item.status),
  joinedAt: item.createdAt ? new Date(item.createdAt) : null,
});
